import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartOrderItem, DataType } from '../../utils/types/analytics/analytics_types';
import { LightColors, MediumColors } from './Colors';
import { DashletIconGroup } from './DashletIconGroup';

const colors = [
  MediumColors.GREEN, MediumColors.BLUE, MediumColors.ORANGE, MediumColors.RED, MediumColors.FUCHSIA,
  MediumColors.PURPLE, MediumColors.CYAN, MediumColors.PINK, MediumColors.YELLOW, MediumColors.LIME,
  LightColors.GREEN, LightColors.BLUE, LightColors.ORANGE, LightColors.RED, LightColors.FUCHSIA,
  LightColors.PURPLE, LightColors.CYAN, LightColors.PINK, LightColors.YELLOW, LightColors.LIME,
];

type TootltipPayload = {
  color: string;
  dataKey: string;
  fill: string;
  value: number;
  name: string;
};

type ColumnChartDashletProps = {
  id: string;
  data: Array<DataType>;
  title: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const ColumnChartDashlet = ({
  id,
  data,
  title,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: ColumnChartDashletProps) => {
  const [transformedData, setTransformedData] = useState<{ [key: string]: any }[]>([]);
  const [categories, setCategories] = useState<{ [key: string]: string }>({});
  const [tooltipPayload, setTooltipPayload] = useState<TootltipPayload | null>(null);

  useEffect(() => {
    const d: { [key: string]: any } = {
      name: title,
    };
    const cat: { [key: string]: any } = {};
    if (data !== undefined) {
      for (let item of data) {
        cat[item.name] = item.name;
        d[item.name] = item.value * 100;
      }
    }

    setCategories(cat);
    setTransformedData([d]);
  }, [data, title]);

  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  const handleMouseEnter = (data: any) => {
    if (data.tooltipPayload && data.tooltipPayload.length > 0) {
      setTooltipPayload(data.tooltipPayload[0]);
    }
  };

  const handleMouseLeave = (data: any) => {
    if (data.tooltipPayload && data.tooltipPayload.length > 0) {
      setTooltipPayload(null);
    }
  };

  const CustomTooltip = (props: any) => {
    if (props.active && props.payload && props.payload.length && tooltipPayload) {
      let value: string | number = tooltipPayload.value;
      if (typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value)) {
        value = value.toFixed(3);
      }
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: tooltipPayload.color || 'text.primary' }}>
            {tooltipPayload.name}: {value}
          </Typography>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        width: '100%',
        gridColumn: { xs: 'span 1', lg: 'span 2' },
        borderRadius: '16px',
        height: '100%',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '1.5rem' : '0.875rem',
            color: 'text.secondary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {!data || data.length === 0 ? (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              No data to preview
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={fullScreenDetected ? 500 : 300}>
              <BarChart
                data={transformedData}
                margin={{
                  top: 20,
                  right: 20,
                  left: 10,
                  bottom: 30,
                }}
                barSize={20}
              >
                <XAxis dataKey="name" scale="auto" fontSize={12} />
                <YAxis />
                <Legend />
                <Tooltip content={<CustomTooltip />} />
                <CartesianGrid strokeDasharray="3 3" />
                {Object.keys(categories).map((category, index: number) => (
                  <Bar
                    key={index}
                    dataKey={category}
                    fill={colors[index % colors.length]}
                    background={{ fill: '#eee' }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
